import { useRef, useState } from "react";
import { Loader2, Camera, X, Check, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { toast } from "./ui/sonner";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { Button } from "./ui/button";

export const PhotoUploader = ({ photos, onChange, testId }) => {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null);
  const online = useOnlineStatus();

  const handleFiles = async (files) => {
    if (!navigator.onLine) {
      toast.warning("Hors ligne", { description: "Connectez-vous pour ajouter des photos." });
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/upload-photo", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      onChange([...(photos || []), ...uploaded]);
    } catch (e) {
      console.error("upload error", e);
      toast.error("Échec de l'envoi de la photo", {
        description: "La photo n'a pas pu être enregistrée. Réessayez, ou signalez ce problème à un administrateur si cela persiste.",
      });
    } finally {
      setUploading(false);
    }
  };

  const openPreview = (files, source) => {
    if (!files.length) return;
    setPending({ files, previews: files.map((f) => URL.createObjectURL(f)), source });
  };

  const closePending = () => {
    pending?.previews.forEach((url) => URL.revokeObjectURL(url));
    setPending(null);
  };

  const confirmPending = async () => {
    const files = pending.files;
    closePending();
    await handleFiles(files);
  };

  const retake = () => {
    const source = pending?.source;
    closePending();
    if (source === "camera") cameraInputRef.current?.click();
    else galleryInputRef.current?.click();
  };

  return (
    <div data-testid={testId} className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {(photos || []).map((url, idx) => (
          <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-700">
            <img src={url} alt="photo" className="w-full h-full object-cover" />
            <button
              type="button"
              data-testid={`photo-remove-${idx}`}
              onClick={() => onChange(photos.filter((_, i) => i !== idx))}
              className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid={`${testId}-camera-button`}
          onClick={() => cameraInputRef.current?.click()}
          className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center text-slate-400 hover:border-cyan-500 hover:text-cyan-400 transition-colors"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
        </button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => { const files = Array.from(e.target.files); e.target.value = ""; openPreview(files, "camera"); }}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { const files = Array.from(e.target.files); e.target.value = ""; openPreview(files, "gallery"); }}
        />
      </div>
      <button
        type="button"
        data-testid={`${testId}-gallery-button`}
        onClick={() => galleryInputRef.current?.click()}
        className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-cyan-400 underline underline-offset-2"
      >
        ou choisir depuis la galerie
      </button>
      {!online && <p className="text-[10px] text-amber-400 font-mono uppercase tracking-widest">Photos indisponibles hors ligne</p>}

      {pending && (
        <div data-testid={`${testId}-preview-modal`} className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4 gap-4">
          <button
            type="button"
            data-testid={`${testId}-preview-cancel`}
            onClick={closePending}
            className="absolute top-4 right-4 text-slate-400 hover:text-white"
          >
            <X className="w-7 h-7" />
          </button>
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Aperçu — vérifiez la netteté avant de valider</p>
          <div className="flex-1 w-full max-w-md overflow-y-auto flex flex-wrap items-center justify-center gap-3">
            {pending.previews.map((url, idx) => (
              <img key={idx} src={url} alt="aperçu" className="max-h-[60vh] max-w-full rounded-lg border border-slate-700 object-contain" />
            ))}
          </div>
          <div className="flex gap-3 w-full max-w-md">
            <Button
              type="button"
              data-testid={`${testId}-preview-retake-button`}
              onClick={retake}
              variant="outline"
              className="flex-1 border-slate-700 text-slate-200 font-bold uppercase text-xs"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reprendre
            </Button>
            <Button
              type="button"
              data-testid={`${testId}-preview-confirm-button`}
              onClick={confirmPending}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-xs"
            >
              <Check className="w-4 h-4 mr-1.5" /> Valider
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
